import {createStyles, Radio, Table, Text} from '@mantine/core';
import React, {useEffect, useState} from 'react';

import {InstrumentQueryStatus, SnowflakeTable} from 'instruments/instrument-data/InstrumentDataConstants';
import {
  createListTablesInstrumentQuery,
  pollInstrumentQueryStatus,
} from 'instruments/instrument-data/InstrumentDataDAO';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

const useStyles = createStyles(() => ({
  selectableRow: {
    cursor: 'pointer',
  },
}));

interface SnowflakeTableSelectorProps {
  connectionId: string;
  value: SnowflakeTable | null;
  onChange: (table: SnowflakeTable | null) => void;
}

// Lists the tables available in the connected Snowflake database (via the LIST_TABLES
// query) and lets the user pick one to export.
export default function SnowflakeTableSelector({
  connectionId,
  value,
  onChange,
}: SnowflakeTableSelectorProps): JSX.Element {
  const {classes} = useStyles();
  const [tables, setTables] = useState<Array<SnowflakeTable> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setTables(null);

    createListTablesInstrumentQuery(connectionId)
      .then(({instrumentQueryId}) => pollInstrumentQueryStatus(instrumentQueryId))
      .then((query) => {
        if (cancelled) {
          return;
        }
        if (query.status === InstrumentQueryStatus.ERROR) {
          setError(query.info || 'Failed to list tables');
        } else {
          // The LIST_TABLES result is returned in the query's `values` payload as
          // `{tables: [...]}` (`info` is just the status string, e.g. "Success").
          const values = (query.values ?? {}) as {tables?: Array<SnowflakeTable>};
          setTables(values.tables ?? []);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [connectionId]);

  if (isLoading) {
    return (
      <>
        <Text size="sm" color="dimmed">
          Loading tables from Snowflake...
        </Text>
        <CenteredHorizontalThrobber />
      </>
    );
  }

  if (error) {
    return (
      <Text size="sm" color="red">
        Error loading tables: {error}
      </Text>
    );
  }

  if (!tables || tables.length === 0) {
    return (
      <Text size="sm" color="dimmed">
        No tables found in the connected database.
      </Text>
    );
  }

  const selectedKey = value ? `${value.schema_name}.${value.table_name}` : null;

  return (
    <Table highlightOnHover={true}>
      <thead>
        <tr>
          <th />
          <th>Schema</th>
          <th>Table</th>
        </tr>
      </thead>
      <tbody>
        {tables.map((table) => {
          const key = `${table.schema_name}.${table.table_name}`;
          const isSelected = selectedKey === key;
          const toggle = (): void => {
            onChange(isSelected ? null : table);
          };
          return (
            <tr key={key} className={classes.selectableRow} onClick={toggle}>
              <td>
                {/* Selection is driven by clicking the row; the radio is a controlled,
                    accessible reflection of that state (readOnly so it doesn't handle
                    its own change and double-toggle). */}
                <Radio
                  checked={isSelected}
                  readOnly={true}
                  aria-label={`Select ${table.schema_name}.${table.table_name}`}
                />
              </td>
              <td>{table.schema_name}</td>
              <td>{table.table_name}</td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}
